import {
  Center,
  Code,
  Separator,
  Heading,
  Link,
  Text,
  Card,
  Box,
  Flex,
  HStack,
  Image,
  VStack,
} from '@chakra-ui/react';
import { Link as ReactRouterLink } from 'react-router-dom';
import { Message, Attachment } from '../types';
import { useUserColors } from '../hooks/useUserColors';
import { openAttachmentInNewTab, useAttachmentObjectUrl } from './attachments';
import { linkify } from './linkify';
import { LinkColorForColor } from './colors';
import React from 'react';

const wrap = (s: string) =>
  s.replace(/(?![^\n]{1,80}$)([^\n]{1,80})\s/g, '$1\n');

const AttachmentMedia = ({ attachment }: { attachment: Attachment }) => {
  const { objectUrl, loading, error } = useAttachmentObjectUrl(attachment.url);

  if (loading && !objectUrl) {
    return <Text fontSize='sm' opacity={0.8}>Loading attachment…</Text>;
  }

  if (error) {
    return <Text color='red.200'>Failed to load {attachment.filename}</Text>;
  }

  if (!objectUrl) {
    return null;
  }

  if (attachment.type === 'image') {
    return (
      <Center>
        <Image
          src={objectUrl}
          alt={attachment.filename}
          maxW='100%'
          height='auto'
          borderRadius='xl'
          boxShadow='0 12px 32px rgba(0, 0, 0, 0.35)'
        />
      </Center>
    );
  }

  return (
    <Center>
      <video
        src={objectUrl}
        controls
        style={{
          maxWidth: '100%',
          height: 'auto',
          borderRadius: '12px',
          boxShadow: '0 12px 32px rgba(0, 0, 0, 0.35)',
        }}
      >
        Your browser does not support the video tag.
      </video>
    </Center>
  );
};

const AttachmentDisplay = ({ attachments }: { attachments: Attachment[] }) => {
  if (!attachments || attachments.length === 0) {
    return null;
  }

  return (
    <VStack gap={4} mt={5} align='stretch'>
      {attachments.map((attachment, index) => {
        if (attachment.type === 'image' || attachment.type === 'video') {
          return <AttachmentMedia key={index} attachment={attachment} />;
        }

        return (
          <Box key={index}>
            <Link
              as='button'
              type='button'
              px={3}
              py={1.5}
              borderRadius='lg'
              bg='rgba(0, 0, 0, 0.2)'
              fontWeight='600'
              onClick={() => {
                void openAttachmentInNewTab(attachment.url);
              }}
            >
              📎 {attachment.filename}
            </Link>
          </Box>
        );
      })}
    </VStack>
  );
};

export const FullMessageCard = (message: Message) => {
  const { getColors } = useUserColors();
  const { bg: cardColor, text: textColor } = getColors(message.name);
  const linkColor = LinkColorForColor(cardColor);
  if (!message) {
    return <></>;
  }
  return (
    <Card.Root
      bg={cardColor}
      color={textColor}
      variant='elevated'
      w='100%'
      maxW='3xl'
      borderRadius='2xl'
      boxShadow='0 20px 50px rgba(0, 0, 0, 0.45)'
      border='1px solid rgba(255, 255, 255, 0.16)'
      overflow='hidden'
    >
      <Card.Body p={{ base: 4, md: 6 }} position='relative'>
        <Flex
          direction={{ base: 'column', sm: 'row' }}
          justify='space-between'
          align={{ base: 'start', sm: 'center' }}
          gap={2}
          mb={3}
        >
          <HStack gap={2} flexWrap='wrap'>
            {message.parent_id ? (
              <Link asChild>
                <ReactRouterLink to={'/message/' + message.parent_id.toString()}>
                  <Box
                    as='span'
                    px={2.5}
                    py={1}
                    borderRadius='full'
                    bg='rgba(0, 0, 0, 0.22)'
                    fontSize='xs'
                    fontWeight='700'
                  >
                    ↑ Parent Message
                  </Box>
                </ReactRouterLink>
              </Link>
            ) : null}
          </HStack>

          <Link asChild>
            <ReactRouterLink to={`/message_tree/${message.id}`}>
              <Box
                as='span'
                px={3}
                py={1}
                bg='rgba(0, 0, 0, 0.22)'
                _hover={{ bg: 'rgba(0, 0, 0, 0.35)' }}
                borderRadius='full'
                fontSize='xs'
                fontWeight='700'
                transition='all 0.2s'
                display='inline-flex'
                alignItems='center'
                gap={1.5}
              >
                🧵 View 3D Thread Tree →
              </Box>
            </ReactRouterLink>
          </Link>
        </Flex>

        <Heading
          as='h2'
          size={{ base: 'md', md: 'lg' }}
          fontWeight='800'
          lineHeight='1.3'
          mb={2}
        >
          {message.first ? '' : 'RE: '}
          {message.subject}
        </Heading>

        <HStack justify='space-between' flexWrap='wrap' gap={2} opacity={0.9}>
          <Heading as='h3' size='sm' fontWeight='700'>
            From: {message.name}
          </Heading>
          <Heading as='h4' size='xs' fontFamily='mono'>
            {message.date}
          </Heading>
        </HStack>

        <Center height='24px'>
          <Separator orientation='horizontal' opacity={0.3} />
        </Center>

        <Code
          display='block'
          whiteSpace='pre-wrap'
          wordBreak='break-word'
          color={textColor}
          bg='rgba(0, 0, 0, 0.12)'
          p={{ base: 3, md: 4 }}
          borderRadius='xl'
          fontSize={{ base: 'xs', md: 'sm' }}
          lineHeight='1.6'
        >
          {linkify(wrap(message.body), linkColor)}
        </Code>
        {message.has_attachment && message.attachments && (
          <AttachmentDisplay attachments={message.attachments} />
        )}
      </Card.Body>
    </Card.Root>
  );
};

const getSnippet = (text: string, terms: string[]): React.ReactNode => {
  if (!terms || terms.length === 0 || !text) return text;

  const lowerText = text.toLowerCase();
  let minIndex = -1;
  let matchTerm = '';

  for (const term of terms) {
    if (!term) continue;
    const idx = lowerText.indexOf(term.toLowerCase());
    if (idx !== -1) {
      if (minIndex === -1 || idx < minIndex) {
        minIndex = idx;
        matchTerm = term;
      }
    }
  }

  if (minIndex === -1) return text;

  const windowSize = 80;
  const start = Math.max(0, minIndex - windowSize);
  const end = Math.min(text.length, minIndex + matchTerm.length + windowSize);

  let snippet = text.substring(start, end);

  if (start > 0) snippet = '...' + snippet;
  if (end < text.length) snippet = snippet + '...';

  const escapedTerms = terms
    .map(t => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .filter(t => t.length > 0);
  if (escapedTerms.length === 0) return snippet;

  const pattern = `(${escapedTerms.join('|')})`;
  const regex = new RegExp(pattern, 'gi');

  const parts = snippet.split(regex);

  return (
    <>
      {parts.map((part, i) => {
        if (terms.some(t => t.toLowerCase() === part.toLowerCase())) {
          return (
            <Box
              fontWeight='bold'
              as='span'
              bg='yellow.300'
              color='black'
              px={1}
              rounded='sm'
              key={i}
            >
              {part}
            </Box>
          );
        }
        return <span key={i}>{part}</span>;
      })}
    </>
  );
};

export const CompactMessageCard = (
  message: Message & {
    noLink?: boolean;
    showSubject?: boolean;
    highlightTerms?: string[];
  }
) => {
  const { getColors } = useUserColors();
  const { bg: cardColor, text: textColor } = getColors(message.name);
  if (!message) {
    return <></>;
  }

  const hasHighlights =
    message.highlightTerms && message.highlightTerms.length > 0;

  const content = (
    <Card.Root
      variant='elevated'
      w='100%'
      maxW='3xl'
      bg={cardColor}
      color={textColor}
      borderRadius='xl'
      border='1px solid rgba(255, 255, 255, 0.14)'
      boxShadow='0 8px 24px rgba(0, 0, 0, 0.3)'
      transition='all 0.2s cubic-bezier(0.16, 1, 0.3, 1)'
      _hover={
        message.noLink
          ? undefined
          : {
              transform: 'translateY(-2px)',
              boxShadow: '0 14px 32px rgba(0, 0, 0, 0.45)',
            }
      }
    >
      <Card.Body p={{ base: 3.5, md: 4 }} position='relative'>
        {message.has_attachment && (
          <Box
            position='absolute'
            top={3}
            right={3}
            title='Has attachment'
            px={2}
            py={0.5}
            borderRadius='full'
            bg='rgba(0, 0, 0, 0.2)'
            fontSize='xs'
          >
            📎
          </Box>
        )}
        <HStack
          justify='space-between'
          pr={message.has_attachment ? 8 : 0}
          mb={1}
          flexWrap='wrap'
        >
          <Heading as='h3' size='sm' fontWeight='800'>
            {message.name}
          </Heading>
          <Text fontSize='xs' fontFamily='mono' opacity={0.85}>
            {message.date}
          </Text>
        </HStack>
        {message.showSubject && (
          <Heading as='h3' size='xs' mb={1.5} opacity={0.92}>
            <i>
              {message.first ? '' : 'RE: '}
              {message.subject}
            </i>
          </Heading>
        )}
        <Text
          color={textColor}
          fontSize='sm'
          lineClamp={hasHighlights ? 2 : 1}
          opacity={0.95}
        >
          {hasHighlights
            ? getSnippet(message.body, message.highlightTerms!)
            : message.body}
        </Text>
      </Card.Body>
    </Card.Root>
  );

  if (message.noLink) {
    return (
      <Box mb={3} w='100%' maxW='3xl'>
        {content}
      </Box>
    );
  }

  return (
    <Box mb={3} w='100%' maxW='3xl'>
      <Link textDecoration='none' display='block' w='100%' asChild>
        <ReactRouterLink to={'/message/' + message.id.toString()}>
          {content}
        </ReactRouterLink>
      </Link>
    </Box>
  );
};
